import React, { useState, useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import {
  Box,
  VStack,
  Heading,
  Input,
  Button,
  Text,
  FormControl,
  FormLabel,
  Alert,
  AlertIcon,
  Container
} from '@chakra-ui/react'
import { useAuth } from '../contexts/AuthContext'

export default function LoginPage() {
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState(null)
  const [isLoading, setIsLoading] = useState(false)
  const navigate = useNavigate()
  const location = useLocation()
  const { login, isAuthenticated } = useAuth()

  // Redirect to intended page if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      const from = location.state?.from?.pathname || '/app'
      navigate(from, { replace: true })
    }
  }, [isAuthenticated, navigate, location])

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError(null)

    if (!username || !password) {
      setError('Please enter both username and password')
      return
    }

    setIsLoading(true)
    const result = await login(username, password)
    setIsLoading(false)

    if (result.success) {
      // Navigate to intended page or console home
      const from = location.state?.from?.pathname || '/app'
      navigate(from, { replace: true })
    } else {
      setError(result.error || 'Login failed. Please try again.')
    }
  }

  return (
    <Box
      minH="100vh"
      bgGradient="linear(to-br, #09090b, #131316, #18181b)"
      display="flex"
      alignItems="center"
      justifyContent="center"
      px={4}
    >
      <Container maxW="md">
        <VStack spacing={8}>
          <Heading
            as="h1"
            size="2xl"
            fontWeight="500"
            letterSpacing="-0.01em"
            color="#fafafa"
            textAlign="center"
          >
            First Signal
          </Heading>

          <Box
            w="100%"
            bg="#131316"
            border="1px solid"
            borderColor="#27272a"
            borderRadius="16px"
            p={8}
            boxShadow="0 4px 20px rgba(0, 0, 0, 0.3)"
          >
            <form onSubmit={handleSubmit}>
              <VStack spacing={6}>
                <Text
                  fontSize="xl"
                  fontWeight="500"
                  color="#fafafa"
                  textAlign="center"
                  mb={2}
                >
                  Sign In
                </Text>

                {error && (
                  <Alert
                    status="error"
                    bg="rgba(255, 107, 107, 0.1)"
                    border="1px solid"
                    borderColor="rgba(255, 107, 107, 0.3)"
                    borderRadius="8px"
                  >
                    <AlertIcon color="red.400" />
                    <Text color="red.400" fontSize="sm">{error}</Text>
                  </Alert>
                )}

                <FormControl isRequired>
                  <FormLabel color="#d4d4d8" fontSize="sm" mb={2}>
                    Username
                  </FormLabel>
                  <Input
                    type="text"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    placeholder="Enter your username"
                    bg="#09090b"
                    border="1px solid"
                    borderColor="#27272a"
                    color="#e4e4e7"
                    _placeholder={{ color: '#52525b' }}
                    _hover={{ borderColor: '#3f3f46' }}
                    _focus={{
                      borderColor: '#f97316',
                      boxShadow: '0 0 0 1px #f97316'
                    }}
                    size="lg"
                    autoComplete="username"
                    autoFocus
                  />
                </FormControl>

                <FormControl isRequired>
                  <FormLabel color="#d4d4d8" fontSize="sm" mb={2}>
                    Password
                  </FormLabel>
                  <Input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Enter your password"
                    bg="#09090b"
                    border="1px solid"
                    borderColor="#27272a"
                    color="#e4e4e7"
                    _placeholder={{ color: '#52525b' }}
                    _hover={{ borderColor: '#3f3f46' }}
                    _focus={{
                      borderColor: '#f97316',
                      boxShadow: '0 0 0 1px #f97316'
                    }}
                    size="lg"
                    autoComplete="current-password"
                  />
                </FormControl>

                <Button
                  type="submit"
                  w="100%"
                  size="lg"
                  bgGradient="linear(to-br, #c2410c, #ea580c)"
                  color="#fafafa"
                  border="1px solid"
                  borderColor="#9a3412"
                  _hover={{
                    borderColor: '#fb923c',
                    transform: 'translateY(-1px)',
                    shadow: 'lg',
                  }}
                  _active={{
                    transform: 'translateY(0)',
                  }}
                  isLoading={isLoading}
                  loadingText="Signing in..."
                  isDisabled={!username || !password}
                >
                  Sign In
                </Button>
              </VStack>
            </form>
          </Box>

          <Text fontSize="sm" color="gray.500" textAlign="center">
            Contact your administrator if you need access
          </Text>
        </VStack>
      </Container>
    </Box>
  )
}
